import { useMemo } from 'react';
import { createStore, useStore, type Store } from '../lib/store';
import { onStorageChange, readStringList, scopedKey, writeStringList } from './localDb';

// Customize Home preferences (user_preferences_service.dart:229-287, home_screen.dart:101-228).
// Per library scope: `disabled_home_sections_v1:{scope}` and
// `compact_home_sections_v1:{scope}`, each a sorted, trimmed, de-duplicated
// string list; the key is removed when the list is empty.

export const DISABLED_HOME_SECTIONS_PREFIX = 'disabled_home_sections_v1';
export const COMPACT_HOME_SECTIONS_PREFIX = 'compact_home_sections_v1';

/** The only section with a compact layout (`supportsCompactLayout`). */
export const COMPACT_CAPABLE_SECTION = 'continue-listening';

/** Home's section-id normalisation (`home_screen.dart:192-198`). */
export function normalizeHomeSectionId(id: string): string {
  return id
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Sorted, trimmed, non-empty, unique (the persisted form). */
export function normalizeSectionList(ids: Iterable<string>): string[] {
  const set = new Set<string>();
  for (const id of ids) {
    const t = typeof id === 'string' ? id.trim() : '';
    if (t) set.add(t);
  }
  return [...set].sort();
}

export interface HomeSectionPrefs {
  disabled: ReadonlySet<string>;
  compact: ReadonlySet<string>;
}

export const disabledHomeSectionsKey = (scope: string) => scopedKey(DISABLED_HOME_SECTIONS_PREFIX, scope);
export const compactHomeSectionsKey = (scope: string) => scopedKey(COMPACT_HOME_SECTIONS_PREFIX, scope);

function readSet(key: string): ReadonlySet<string> {
  return new Set(normalizeSectionList(readStringList(key).map(normalizeHomeSectionId)));
}

export function loadHomeSectionPrefs(scope: string): HomeSectionPrefs {
  return { disabled: readSet(disabledHomeSectionsKey(scope)), compact: readSet(compactHomeSectionsKey(scope)) };
}

const EMPTY: HomeSectionPrefs = Object.freeze({ disabled: new Set<string>(), compact: new Set<string>() });
const emptyStore = createStore<HomeSectionPrefs>(EMPTY);
const stores = new Map<string, Store<HomeSectionPrefs>>();

export function homeSectionPrefsStore(scope: string | null): Store<HomeSectionPrefs> {
  const key = scope?.trim();
  if (!key) return emptyStore;
  let store = stores.get(key);
  if (!store) {
    store = createStore(loadHomeSectionPrefs(key));
    stores.set(key, store);
  }
  return store;
}

onStorageChange(
  (key) => key.startsWith(`${DISABLED_HOME_SECTIONS_PREFIX}:`) || key.startsWith(`${COMPACT_HOME_SECTIONS_PREFIX}:`),
  () => reloadHomeSectionPrefs(),
);

/** Re-reads every loaded scope (another tab, backup import). */
export function reloadHomeSectionPrefs(): void {
  for (const [scope, store] of stores) store.set(loadHomeSectionPrefs(scope));
}

function update(scope: string | null, kind: 'disabled' | 'compact', id: string, member: boolean): boolean {
  const key = scope?.trim();
  const sectionId = normalizeHomeSectionId(id);
  if (!key || !sectionId) return false;
  const store = homeSectionPrefsStore(key);
  const current = store.get()[kind];
  if (current.has(sectionId) === member) return false;
  const next = new Set(current);
  if (member) next.add(sectionId);
  else next.delete(sectionId);
  const list = normalizeSectionList(next);
  store.set({ ...store.get(), [kind]: new Set(list) });
  writeStringList(kind === 'disabled' ? disabledHomeSectionsKey(key) : compactHomeSectionsKey(key), list);
  return true;
}

/** Shows or hides a Home section. Returns false when nothing changed. */
export function setHomeSectionEnabled(scope: string | null, sectionId: string, enabled: boolean): boolean {
  return update(scope, 'disabled', sectionId, !enabled);
}

/** Compact cards on/off (only meaningful for `continue-listening`). */
export function setHomeSectionCompact(scope: string | null, sectionId: string, compact: boolean): boolean {
  return update(scope, 'compact', sectionId, compact);
}

export interface HomeSectionToggle {
  id: string;
  label: string;
  enabled: boolean;
  supportsCompactLayout: boolean;
  useCompactLayout: boolean;
}

/**
 * Customize Home rows: every shelf (hidden ones included, in rank order) by
 * normalised id, then Recent Series and Newest Authors (`home_screen.dart:230-260`).
 */
export function buildHomeSectionToggles(shelves: ReadonlyArray<{ id?: string | null; label: string }>, prefs: HomeSectionPrefs): HomeSectionToggle[] {
  const labels = new Map<string, string>();
  for (const shelf of shelves) {
    const id = normalizeHomeSectionId(shelf.id || shelf.label);
    if (id && !labels.has(id)) labels.set(id, shelf.label);
  }
  if (!labels.has('recent-series')) labels.set('recent-series', 'Recent Series');
  if (!labels.has('recent-authors')) labels.set('recent-authors', 'Newest Authors');
  return [...labels].map(([id, label]) => ({
    id,
    label,
    enabled: !prefs.disabled.has(id),
    supportsCompactLayout: id === COMPACT_CAPABLE_SECTION,
    useCompactLayout: prefs.compact.has(id),
  }));
}

export interface HomeSectionPrefsView extends HomeSectionPrefs {
  isEnabled: (sectionId: string) => boolean;
  isCompact: (sectionId: string) => boolean;
  setEnabled: (sectionId: string, enabled: boolean) => boolean;
  setCompact: (sectionId: string, compact: boolean) => boolean;
}

/** Live Home section preferences for a library scope (updates from Settings apply immediately). */
export function useHomeSectionPrefs(scope: string | null): HomeSectionPrefsView {
  const prefs = useStore(homeSectionPrefsStore(scope));
  return useMemo(
    () => ({
      ...prefs,
      isEnabled: (id: string) => !prefs.disabled.has(normalizeHomeSectionId(id)),
      isCompact: (id: string) => prefs.compact.has(normalizeHomeSectionId(id)),
      setEnabled: (id: string, enabled: boolean) => setHomeSectionEnabled(scope, id, enabled),
      setCompact: (id: string, compact: boolean) => setHomeSectionCompact(scope, id, compact),
    }),
    [prefs, scope],
  );
}
